import Vue from 'vue'

// Setup tab/window close attempt prompt to prevent ongoing downloads aborting. Function here as
// dispatch functions can't directly reference each other and event listener remove needs
// reference to same function
const windowClosePromptHandler = function(event) {
  event.preventDefault()
  return (event.returnValue = "")
}

const s3uploader = {
  namespaced: true,

  state: {
    currentUpload: null,
    uploadClient: null,
    uploadProgress: "",
    uploads: [], // List of active upload objects
  },

  mutations: {
    pushUpload(state, payload) {
      console.log("Adding new upload item", payload)
      state.uploads = state.uploads.concat([payload.newUpload])
    },
    removeUpload(state, payload) {
      state.uploads = state.uploads.filter(item => item.key !== payload.fileKey)
    },
    setUploads(state, payload) {
      state.uploads = payload.newUploads
    },
    setCurrentUpload(state, payload) {
      state.currentUpload = payload.newUpload
    },
    setUploadProgress(state, payload) {
      state.uploadProgress = payload.newProgress
    },
    setUploadClient(state, payload) {
      state.uploadClient = payload.newClient
    }
  },

  actions: {
    /**
     * Add new upload to queue for file in payload
     *
     * @param {object}   context Module context
     * @param {function} context.commit
     * @param {object} payload Content data id, form field name and file upload field
     */
    addUpload({ dispatch, commit, _getters }, payload) {
      return new Promise((resolve, reject) => {
        // Validate parameters
        if(!payload.dataId) { reject("Upload data id missing") }
        if(!payload.fieldName) { reject("Upload data field name missing") }
        if(!payload.fileKey) { reject("Upload data file key missing") }
        if(!payload.fileObj) { reject("Upload data file missing") }

        let startProcessing = false
        if (typeof(payload.startProcessing) !== "undefined") {
          startProcessing = payload.startProcessing
        }

        // Push into queue list
        // TODO Add retry count and last try timestamp?
        let upload = {key: payload.fileKey, dataId: payload.dataId, fieldName: payload.fieldName, file: payload.fileObj}
        commit('pushUpload', {newUpload: upload})

        // Start upload processing if requested
        if (startProcessing) {
          dispatch('processUpload')
        }

        resolve()
      })
    },

    processUpload({dispatch, commit, getters, rootState}) {
      // TODO Check queue size and current download item, add/remove window close prompt if needed

      return new Promise((resolve, reject) => {
        // Check if currently uploading and return early
        if (getters.currentUpload !== null) { resolve(); return }
        if (getters.uploads.length == 0) { resolve(); return }

        // Not uploading and there is items in queue, pop first from queue list
        let upload = getters.uploads[0]
        commit('setCurrentUpload', {newUpload: upload})
        commit('setUploadProgress', {newProgress: ""})

        let restOfUploads = getters.uploads.slice(1)
        commit('setUploads', {newUploads: restOfUploads})

        // Connect and join channel for URL request
        // Using Vue.$socket gives error "TypeError: vue__WEBPACK_IMPORTED_MODULE_0__.default.$socket is undefined"
        let topic = "cms:list_data:" + upload.dataId

        // A second join+leave on a topic the editor already has joined makes the server close the
        // editor's channel, so list data updates stop arriving. Reuse the joined channel if present.
        let shared = Vue.Socket.channels[topic]
        let useShared = !!(shared && shared.channel && shared.channel.state === "joined")
        let channel = useShared ? shared.channel : Vue.prototype.$socket.channel(topic)

        if (!useShared) {
          channel.join()
            .receive("error", (msg) => {
              console.log("Uploader channel join failed", msg)
              dispatch('handleUploadError', { event: event, error: "Uploader channel join error" })

              reject({type: "error", msg: "Uploader channel join failed"})
            })
            .receive("timeout", (msg) => {
              console.log("Uploader channel join timed out", msg)
              dispatch('handleUploadError', { event: event, error: "Uploader channel join timeout" })

              reject({type: "network_issue", msg: "Networking issue..."})
            })
        }

        // Request presigned S3 URL
        let msg_payload = {data_id: upload.dataId, remote_path: upload.key}
        channel.push("get_aws_presigned_url", msg_payload)
          .receive("ok", (msg) => {
            if (!useShared) channel.leave()

            // Presigned URL and form fields were generated successfully in backend
            let s3Url = msg.url
            let requiredFields = msg.fields

            // Setup form data for upload with fields from presigned URL generation
            // File has to be last field, https://docs.aws.amazon.com/AmazonS3/latest/API/sigv4-post-example.html
            let formData = new FormData()
            Object.entries(requiredFields).forEach(([key, value]) => {
              formData.append(key, value)
            })
            formData.append("file", upload.file)

            // Setup XHR client with presigned URL and callbacks
            let client = new XMLHttpRequest()
            client.open("POST", s3Url, true)

            // Attach progress handler to "client.upload" property, "client" reports only at the end of send
            client.upload.onprogress = (event) => { dispatch('handleUploadProgress', { event: event }) }

            client.onload = (event) => {
              // Check for success, S3 POST returns "204 No Content"
              if (client.status === 200 || client.status === 204) {
                dispatch('handleUploadFinished', { event: event })
              } else {
                dispatch('handleUploadError', { event: event, error: "Upload HTTP error " + client.status.toString() })
              }
            }
            client.onerror = (event) => {
              dispatch('handleUploadError', { event: event, error: "Upload HTTP error " + client.status.toString() })
            }
            client.ontimeout = (event) => {
              dispatch('handleUploadError', { event: event, error: "Upload timeout error" })
            }
            client.onabort = (event) => { console.log("Upload aborted") }

            // Start upload, call is non-blocking
            client.send(formData)
            commit('setUploadClient', {newClient: client})

            dispatch('addWindowClosePrompt')

            resolve()
          })
          .receive("error", (msg) => {
            console.log("Presigned url error:", msg)

            // Re-add upload to back of queue
            commit('setCurrentUpload', {newUpload: null})
            commit('pushUpload', {newUpload: upload})

            msg.type = "error"
            reject(msg)
          })
          .receive("timeout", () => {
            console.log("Networking issue...")

            // Re-add upload to back of queue
            commit('setCurrentUpload', {newUpload: null})
            commit('pushUpload', {newUpload: upload})

            reject({type: "network_issue", msg: "Networking issue..."})
          })
      })
    },

    cancelCurrentUpload({ dispatch, getters, commit }, payload) {
      // Stop HTTP client send
      getters.uploadClient.abort()

      // Remove upload data and clean up
      commit('setUploadProgress', {newProgress: ""})
      commit('setCurrentUpload', {newUpload: null})
      commit('setUploadClient', {newClient: null})

      // Restart processing as there might be items in queue
      dispatch('removeWindowClosePrompt')
      dispatch('processUpload')
    },

    removeQueuedUpload({ getters, commit }, payload) {
      if (getters.uploads.length == 0) { return true }

      if (typeof(payload.dataId) == "undefined") {
        console.error("Uploader: data id missing")
        return false
      }
      if (typeof(payload.fieldName) == "undefined") {
        console.error("Uploader: field name missing")
        return false
      }

      // Filter uploads list with payload list data id and field name
      let newUploads = getters.uploads.filter(
        item => item.dataId !== payload.dataId && item.fieldName !== payload.fieldName
      )

      // Something was filtered out, update state
      if (getters.uploads.length != newUploads.length) {
        commit('setUploads', {newUploads: newUploads})
      }

      return true
    },

    handleUploadProgress({ getters, commit }, payload) {
      let event = payload.event

      if (event.lengthComputable) {
        let percent = Math.round((event.loaded / event.total) * 100)
        let percentString = percent.toString() + "%"

        if (percentString !== getters.uploadProgress) {
          commit('setUploadProgress', {newProgress: percentString})
        }
      }
    },

    handleUploadFinished({ getters, commit, dispatch }, payload) {
      console.log("Upload finished:", payload)

      let currentUpload = getters.currentUpload
      let filename = currentUpload.file.name

      // Display notification
      // Using Vue.prototype.$message gives error "TypeError: vue__WEBPACK_IMPORTED_MODULE_0__.default.$message is undefined"
      Vue.prototype.$message.success("Upload: finished " + filename)

      // Remove upload from list
      commit('setUploadProgress', {newProgress: ""})
      commit('setCurrentUpload', {newUpload: null})
      commit('setUploadClient', {newClient: null})

      dispatch('removeWindowClosePrompt')
      dispatch('processUpload')
    },

    handleUploadError({ getters, commit }, payload) {
      let currentUpload = getters.currentUpload
      console.log("Upload error:", currentUpload, payload)

      if (typeof(currentUpload) != "undefined") {
        // Display alert
        let filename = "???"
        if (typeof(currentUpload.file) !== "undefined" && typeof(currentUpload.file.name) !== "undefined") {
          filename = currentUpload.file.name
        }
        Vue.prototype.$message.success("Upload: error " + filename)

        // Remove upload from list after timeout?
        commit('pushUpload', {newUpload: getters.currentUpload})
      }

      if (currentUpload != null) {
        commit('setCurrentUpload', {newUpload: null})
      }
      commit('setUploadClient', {newClient: null})

      dispatch('removeWindowClosePrompt')
      dispatch('processUpload')
    },

    addWindowClosePrompt(_context) {
      window.addEventListener("beforeunload", windowClosePromptHandler, { capture: true })
    },
    removeWindowClosePrompt(_context) {
      window.removeEventListener("beforeunload", windowClosePromptHandler, { capture: true })
    },
  },

  getters: {
    uploads(state, _getters, _rootState, _rootGetters) { return state.uploads },
    currentUpload(state, _getters, _rootState, _rootGetters) { return state.currentUpload },
    uploadProgress(state, _getters, _rootState, _rootGetters) { return state.uploadProgress },
    uploadClient(state, _getters, _rootState, _rootGetters) { return state.uploadClient }
  }
}

export { s3uploader }
